import classNames from 'classnames';
import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactElement, Ref } from 'react';
import styles from './TextField.module.scss';
export type TextFieldType = 'text' | 'email' | 'tel' | 'number' | 'date' | 'password' | 'search' | 'url';

/**
 * What `renderInput` receives: every prop the field was given, its `type`,
 * the Helios input class and the forwarded ref, for the input it returns.
 * A formatting component takes the ref its own way: never spread these onto
 * `components/Cleave.js`, a class component, where a spread `ref` binds the
 * class instance, not the input. Give Cleave the other props and the DOM
 * ref as `htmlRef`, an object ref whose `.current` it sets (a callback ref
 * there does nothing), as Field.js's PhoneInput does today.
 */
export type TextFieldInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'style'> & {
  className: string;
  ref: Ref<HTMLInputElement>;
};
export type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'style' | 'type' | 'size' | 'children'> & {
  /** The input type. Defaults to `text`. */
  type?: TextFieldType;
  /**
   * Renders the input element in place of the native one, with the Helios
   * input class: how app code keeps a formatting input on the Helios look.
   * The class reaches only this function, never a public prop.
   */
  renderInput?: (props: TextFieldInputProps) => ReactElement;
  className?: never;
  style?: never;
};

/**
 * A single-line text input on the Helios field look. It renders the
 * `<input>` itself, with no wrapper, so FormGroup's label and error wiring
 * land on it. The error state is `aria-invalid` (FormGroup sets it).
 */
const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField({
  type = 'text',
  renderInput,
  className: _className,
  style: _style,
  ...props
}, ref) {
  const inputProps = {
    ...props,
    type,
    'data-helios-control': 'text',
    className: classNames(styles['helios-text-field']),
    ref
  };
  if (renderInput) return renderInput(inputProps);
  return <input {...inputProps} />;
});
export default TextField;